/**
 * Formula Parser Utility
 * 
 * Parses Excel formulas to extract cross-sheet references.
 * Supports formats like:
 * - ='SheetName'!C5
 * - =Sheet1!A1
 * - =-'IS1'!C5
 * - =+'Sheet Name'!B10
 */

export interface SheetReference {
  sheet: string;
  cell: string;
  col: string;
  row: number;
}

export interface TrialBalanceCriteria {
  class1?: string;
  l1?: string;
  l2?: string;
}

/**
 * Parse a formula string to extract cross-sheet references
 * 
 * @param formula The Excel formula string (e.g., "=-'IS1'!C5")
 * @returns SheetReference object if a cross-sheet reference is found, null otherwise
 */
export const parseSheetReference = (formula: string | null): SheetReference | null => {
  // Return the first cross-sheet reference found anywhere in the formula.
  // Handles nested/wrapped formulas like =IFERROR(IF(IS1!F12="",0,IS1!F12),0)
  // by delegating to the operator-aware scanner used for getAllSheetReferences.
  const refs = getAllSheetReferences(formula);
  return refs.length > 0 ? refs[0] : null;
};

/**
 * Check if a formula contains a cross-sheet reference
 * 
 * @param formula The Excel formula string
 * @returns true if the formula contains a cross-sheet reference
 */
export const hasSheetReference = (formula: string | null): boolean => {
  return parseSheetReference(formula) !== null;
};

/**
 * Get all sheet references from a formula (for formulas with multiple references)
 * 
 * @param formula The Excel formula string
 * @returns Array of SheetReference objects
 */
export const getAllSheetReferences = (formula: string | null): SheetReference[] => {
  if (!formula || typeof formula !== 'string' || !formula.startsWith('=')) {
    return [];
  }

  const references: SheetReference[] = [];
  
  // Global regex to find all cross-sheet references in the formula.
  // Excludes formula operators/delimiters from the sheet-name capture so
  // wrapped formulas like =IFERROR(IF(IS1!F12="",0,IS1!F12),0) don't pick up
  // the wrapping function names. Also excludes '=' so the leading formula
  // sign isn't absorbed into an unquoted sheet name (e.g. =Sheet1!A1).
  const globalRegex = /'?([^='!+\-*/(),]+)'?!([A-Z]+)(\d+)/gi;
  
  let match;
  while ((match = globalRegex.exec(formula)) !== null) {
    const sheetName = match[1].trim();
    const colLetter = match[2].toUpperCase();
    const rowNumber = parseInt(match[3], 10);
    
    references.push({
      sheet: sheetName,
      cell: colLetter + rowNumber,
      col: colLetter,
      row: rowNumber
    });
  }

  return references;
};

const extractTrialBalanceCriterion = (formula: string, column: string): string | undefined => {
  const pattern = new RegExp(
    `'?"?\\s*Trial Balance\\s*'?"?!\\s*${column}:${column}\\s*,\\s*"([^"]+)"`,
    "i"
  );
  const match = formula.match(pattern);
  return match?.[1]?.trim();
};

/**
 * Parse Trial Balance criteria from a formula with COUNTIFS/SUMIFS
 * Example:
 * =COUNTIFS('Trial Balance'!B:B,"IS",'Trial Balance'!D:D,"Revenue from operations",'Trial Balance'!E:E,"Saas Subscription")
 */
export const parseTrialBalanceCriteria = (formula: string | null): TrialBalanceCriteria | null => {
  if (!formula || typeof formula !== "string") {
    return null;
  }

  if (!/trial balance/i.test(formula)) {
    return null;
  }

  const class1 = extractTrialBalanceCriterion(formula, "B");
  const l1 = extractTrialBalanceCriterion(formula, "D");
  const l2 = extractTrialBalanceCriterion(formula, "E");

  if (!class1 && !l1 && !l2) {
    return null;
  }

  return {
    class1,
    l1,
    l2,
  };
};

/**
 * Convert column letter to column index (1-based)
 * 
 * @param col Column letter (e.g., "A", "Z", "AA")
 * @returns Column index (1-based)
 */
export const columnLetterToIndex = (col: string): number => {
  let result = 0;
  for (let i = 0; i < col.length; i++) {
    result = result * 26 + (col.charCodeAt(i) - 'A'.charCodeAt(0) + 1);
  }
  return result;
};

/**
 * Convert column index to column letter (1-based)
 * 
 * @param index Column index (1-based)
 * @returns Column letter (e.g., "A", "Z", "AA")
 */
export const indexToColumnLetter = (index: number): string => {
  let result = '';
  while (index > 0) {
    index--;
    result = String.fromCharCode((index % 26) + 'A'.charCodeAt(0)) + result;
    index = Math.floor(index / 26);
  }
  return result;
};




